import { ApolloError } from '@apollo/client'
import { useAuth0, User } from '@auth0/auth0-react'
import * as R from 'ramda'
import React, { useContext, useEffect, useState } from 'react'
import { tap } from 'rxjs'

import { GetCustomerAccountBalanceDocument } from '../../../graphql/camel'
import { CartFragment } from '../../../graphql/magento'
import useCamelQuery from '../../../hooks/useCamelQuery'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import useUserCategory from '../../../hooks/useUserCategory'
import { setAdsEvent } from '../../../lib/ads/state'
import { analyticsItemsFromCartItems } from '../../../lib/analytics/cart'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { NonEmptyCartContext } from '../../../lib/cart/context'
import useLegalAgreementsGuard from '../../../lib/cart/hooks/useLegalAgreementsGuard'
import usePatientOnboardingCompleteGuard from '../../../lib/cart/hooks/usePatientOnboardingCompleteGuard'
import useRegistrationGuard from '../../../lib/cart/hooks/useRegistrationGuard'
import { formatQuantityError, isQuantityError } from '../../../lib/cart/item-control-options'
import { availableAccountCredit } from '../../../lib/cart/payment'
import {
  applyPlaceOrderTaskParams,
  PaymentDetailsMissingError,
  placeOrderTaskApplication$,
} from '../../../lib/cart/state/order'
import { CompletedCartsContext } from '../../../lib/completed-carts/context'
import { CompletedCart } from '../../../lib/completed-carts/types'
import { getPrivacyControlOptions } from '../../../lib/conversions/actions'
import { conversionsEventItemsFromCartItems } from '../../../lib/conversions/cart'
import { setConversionsEventMeta } from '../../../lib/conversions/state'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { isCyberSourceGatewayError, isCyberSourceValidationError } from '../../../lib/m2/errors'
import { usePageUrl } from '../../../lib/pages'
import { logAndCaptureException } from '../../../utils/errorTools'
import { tapLeft, tapRight } from '../../../utils/rx/operators'
import { userClaimIds } from '../../../utils/userClaims'
import Spinner from '../../Spinner'
import Review from './Review'

const sumAppliedTaxes = R.reduce(
  (
    acc: number,
    appliedTax: NonNullable<NonNullable<CartFragment['prices']>['applied_taxes']>[number],
  ) => (appliedTax ? R.add(acc, appliedTax.amount.value ?? 0) : acc),
  0,
)

const publishPurchaseEvent = (
  { cart, order }: CompletedCart,
  googleAdsEventSendToParam?: string,
  googleAdsPatientEventSendToParam?: string,
): void => {
  const currency = cart.prices?.grand_total?.currency
  const totalValue = cart.prices?.grand_total?.value
  if (currency == null || totalValue == null) {
    return
  }
  setAnalyticsEvent({
    event: 'purchase',
    currency,
    transaction_id: order.order_number,
    value: totalValue,
    affiliation: cart.ww_id ? 'Well World' : undefined,
    coupon: cart.applied_coupons?.[0]?.code,
    shipping: cart.shipping_addresses[0]?.selected_shipping_method?.amount?.value ?? undefined,
    tax: sumAppliedTaxes(cart.prices?.applied_taxes ?? []),
    items: analyticsItemsFromCartItems(cart.items ?? []),
  })

  // Google Ads patient tracking
  if (googleAdsPatientEventSendToParam) {
    setAdsEvent({
      event: 'conversion',
      value: totalValue,
      currency,
      transaction_id: order.order_number,
      send_to: googleAdsPatientEventSendToParam,
    })
  }

  // Google Ads conversion tracking (User Category specific)
  if (googleAdsEventSendToParam) {
    setAdsEvent({
      event: 'conversion',
      value: totalValue,
      currency,
      transaction_id: order.order_number,
      send_to: googleAdsEventSendToParam,
    })
  }

  setConversionsEventMeta({
    event_name: 'Purchase',
    custom_data: {
      content_type: 'product',
      contents: conversionsEventItemsFromCartItems(cart.items ?? []),
      currency,
      order_id: order.order_number,
      value: totalValue,
    },
    ...getPrivacyControlOptions(),
  })
}

type PatientEventSendToParamError = 'LAST_ORDER_DATE_MISSING'

const getGoogleAdsPatientEventSendToParam = (
  user: User | undefined,
  userCategory: string | undefined,
): [string | undefined, PatientEventSendToParamError | undefined] => {
  if (userCategory !== 'patient') {
    return [undefined, undefined]
  }
  const lastOrderDate = user ? user[userClaimIds.lastOrderDate] : undefined
  const notConsulted = user ? user[userClaimIds.notConsulted] : undefined
  if (lastOrderDate === null || lastOrderDate === undefined) {
    return notConsulted
      ? ['not_consulted_returning', 'LAST_ORDER_DATE_MISSING']
      : ['consulted_returning', 'LAST_ORDER_DATE_MISSING']
  }
  if (lastOrderDate === '') {
    return notConsulted ? ['not_consulted_new', undefined] : ['consulted_new', undefined]
  }
  return notConsulted ? ['not_consulted_returning', undefined] : ['consulted_returning', undefined]
}

const ReviewContainer: React.FC = () => {
  const { loadingRedirect, shouldRedirect } = useRegistrationGuard()
  usePatientOnboardingCompleteGuard()
  useLegalAgreementsGuard()
  const customerCart = useContext(NonEmptyCartContext)
  const completedCarts = useContext(CompletedCartsContext)
  const dispensary = useDispensaryContext()
  const { navigate } = usePageUrl()
  const [submitting, setSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [networkError, setNetworkError] = useState(false)
  const [outOfStockError, setOutOfStockError] = useState('')
  const [quantityError, setQuantityError] = useState('')
  const [otherError, setOtherError] = useState(false)
  const { googleAdsEventsByCategory, googleAdsPatientEvents } = useSiteMetadata() ?? {}
  const { user } = useAuth0()
  const { userCategory } = useUserCategory()
  const googleAdsEventSendToParam =
    googleAdsEventsByCategory && userCategory
      ? googleAdsEventsByCategory[userCategory] ?? undefined
      : undefined
  const [googleAdsPatientCategory, googleAdsPatientEventError] =
    getGoogleAdsPatientEventSendToParam(user, userCategory)
  const googleAdsPatientEventSendToParam =
    googleAdsPatientEvents && googleAdsPatientCategory
      ? googleAdsPatientEvents[googleAdsPatientCategory as keyof typeof googleAdsPatientEvents] ??
        undefined
      : undefined

  useEffect(() => {
    if (googleAdsPatientEventError) {
      logAndCaptureException(new Error('lastOrderDate is null or undefined for patient'))
    }
  }, [googleAdsPatientEventError])

  const {
    data: accountBalanceData,
    called: accountBalanceCalled,
    error: accountBalanceError,
    loading: accountBalanceLoading,
  } = useCamelQuery(GetCustomerAccountBalanceDocument, {
    // always fetch fresh customer account history
    fetchPolicy: 'cache-and-network',
  })

  useLogAndCaptureError(accountBalanceError)

  const loadingAccountHistory = !(accountBalanceCalled && !accountBalanceLoading)

  const resetErrors = () => {
    setNetworkError(false)
    setOutOfStockError('')
    setOtherError(false)
    setPaymentError('')
    setQuantityError('')
  }

  const onSubmit = () => {
    resetErrors()
    const grandTotal = customerCart.prices?.grand_total?.value

    if (
      customerCart.selected_payment_method?.code === 'purchaseorder' &&
      grandTotal != null &&
      grandTotal > availableAccountCredit(accountBalanceData?.getCustomerAccountBalance)
    ) {
      setPaymentError('Not enough balance, please use credit card to complete purchase.')
      return
    }

    setSubmitting(true)
    applyPlaceOrderTaskParams({ cart: customerCart, dispensarySlug: dispensary?.slug ?? '' })
  }

  useEffect(() => {
    const subscription = placeOrderTaskApplication$
      .pipe(
        tapLeft(({ error }): void => {
          logAndCaptureException(error)
          if (error instanceof PaymentDetailsMissingError) {
            setPaymentError('Please select a payment method.')
            return
          }
          if (!(error instanceof ApolloError)) {
            setOtherError(true)
            return
          }
          if (error.networkError && !error.graphQLErrors.length) {
            setNetworkError(true)
            return
          }

          // check error message for string indicating CyberSource gateway error
          const cyberSourceErrorMessage = error.graphQLErrors.find(
            ({ message }) =>
              isCyberSourceGatewayError(message) || isCyberSourceValidationError(message),
          )?.message
          if (cyberSourceErrorMessage) {
            setPaymentError(cyberSourceErrorMessage)
            return
          }

          // check error message for quantity
          const quantityErrorMessage = error.graphQLErrors.find(({ message }) =>
            isQuantityError(message),
          )?.message
          if (quantityErrorMessage) {
            setQuantityError(
              formatQuantityError(
                quantityErrorMessage,
                'We were unable to place your order because an item in your cart had the following error',
              ),
            )
            return
          }

          // get error message indicating out of stock error if present
          const outOfStockMessage = error.graphQLErrors.find(({ message }) =>
            message.includes('out of stock'),
          )?.message
          if (outOfStockMessage) {
            setOutOfStockError(outOfStockMessage)
            return
          }

          // some other GraphQL error returned from Magento
          setOtherError(true)
        }),
        tapRight(({ data }) => {
          publishPurchaseEvent(data, googleAdsEventSendToParam, googleAdsPatientEventSendToParam)
          completedCarts.push(data)
          navigate(`/checkout/confirmation/${data.completedAt}`)
        }),
        tap(() => setSubmitting(false)),
      )
      .subscribe()
    return () => subscription.unsubscribe()
  }, [completedCarts, navigate, googleAdsEventSendToParam, googleAdsPatientEventSendToParam])

  if (loadingAccountHistory || loadingRedirect || shouldRedirect) {
    return <Spinner loading />
  }

  return (
    <Review
      networkError={networkError}
      onSubmit={onSubmit}
      outOfStockError={outOfStockError}
      otherError={otherError}
      paymentError={paymentError}
      quantityError={quantityError}
      submitting={submitting}
    />
  )
}

export default ReviewContainer
